import { useEffect, useState, useMemo, useRef } from 'react';
import { api } from '../utils/api';
import Spinner from '../components/Spinner';

// ─── Constants ────────────────────────────────────────────────────────────────

const COST_TYPES = [
  { value: 'advertising', label: 'Reklam',        icon: '🎯' },
  { value: 'shipping',    label: 'Kargo/Nakliye',  icon: '🚢' },
  { value: 'customs',     label: 'Gümrük',         icon: '🏛️' },
  { value: 'storage',     label: 'Depolama',       icon: '📦' },
  { value: 'other',       label: 'Diğer',          icon: '📋' },
];

const CURRENCIES = ['USD', 'TRY', 'EUR', 'RUB'];

const COST_TYPE_MAP = Object.fromEntries(COST_TYPES.map((c) => [c.value, c]));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtPrice(n) {
  if (n === null || n === undefined) return '—';
  return '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(n) {
  if (n === null || n === undefined) return '—';
  return parseFloat(n).toFixed(1) + '%';
}

function marginBadge(pct) {
  if (pct === null || pct === undefined) {
    return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">—</span>;
  }
  const val = parseFloat(pct);
  let cls;
  if (val < 0)  cls = 'bg-red-100 text-red-700';
  else if (val < 20) cls = 'bg-amber-100 text-amber-700';
  else          cls = 'bg-green-100 text-green-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
      {fmtPct(val)}
    </span>
  );
}

// ─── CSV Template ─────────────────────────────────────────────────────────────

function downloadTemplate() {
  const content = [
    'offer_id,cost_type,amount,currency,period_label,note',
    'EXAMPLE-OFFER-ID,advertising,150.00,USD,Mayıs 2026,Facebook reklamı',
    'EXAMPLE-OFFER-ID,shipping,75.50,USD,Mayıs 2026,Kargo ücreti',
  ].join('\n');

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = 'maliyet_sablonu.csv';
  a.click();
  URL.revokeObjectURL(url);
}

function parseCSVPreview(text) {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  return lines.slice(1, 6).map((line) => {
    const cols = line.split(',').map((c) => c.trim().replace(/^"|"$/g, ''));
    return {
      offer_id:     cols[0] || '',
      cost_type:    cols[1] || '',
      amount:       cols[2] || '',
      currency:     cols[3] || 'USD',
      period_label: cols[4] || '',
      note:         cols[5] || '',
    };
  });
}

// ─── CSV Import Modal ─────────────────────────────────────────────────────────

function CsvImportModal({ onClose, onDone }) {
  const [csvText,  setCsvText]  = useState('');
  const [preview,  setPreview]  = useState([]);
  const [result,   setResult]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const fileRef = useRef();

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setCsvText(text);
      setPreview(parseCSVPreview(text));
      setResult(null);
    };
    reader.readAsText(file, 'UTF-8');
  }

  async function handleImport() {
    if (!csvText) { setError('Lütfen bir CSV dosyası seçin'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.importCostsCsv(csvText);
      setResult(res);
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">CSV İçe Aktar</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Download template */}
          <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-xl">
            <span className="text-xl">📄</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-indigo-900">Şablon CSV</div>
              <div className="text-xs text-indigo-600">Örnek format ile şablon indirin</div>
            </div>
            <button
              onClick={downloadTemplate}
              className="text-xs font-medium text-indigo-700 hover:text-indigo-900 underline whitespace-nowrap"
            >
              İndir
            </button>
          </div>

          {/* File input */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">CSV Dosyası Seç</label>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFile}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
            />
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-600 mb-1.5">Önizleme (ilk 5 satır)</div>
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      {['offer_id', 'cost_type', 'amount', 'currency', 'period'].map((h) => (
                        <th key={h} className="px-2 py-1.5 text-left font-medium text-gray-500">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className="border-t border-gray-50">
                        <td className="px-2 py-1.5 font-mono text-gray-800 truncate max-w-[120px]">{row.offer_id}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.cost_type}</td>
                        <td className="px-2 py-1.5 text-gray-800">{row.amount}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.currency}</td>
                        <td className="px-2 py-1.5 text-gray-600">{row.period_label}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="p-3 rounded-xl bg-green-50 border border-green-100">
              <div className="text-sm font-semibold text-green-800 mb-1">
                {result.imported} satır içe aktarıldı, {result.skipped} atlandı
              </div>
              {result.errors && result.errors.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {result.errors.map((e, i) => (
                    <li key={i} className="text-xs text-amber-700">{e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && (
            <div className="p-3 rounded-xl bg-red-50 text-sm text-red-700">{error}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            Kapat
          </button>
          <button
            onClick={handleImport}
            disabled={!csvText || loading}
            className="px-4 py-2 rounded-xl text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
          >
            {loading ? 'İçe Aktarılıyor…' : 'İçe Aktar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cost Detail Drawer ───────────────────────────────────────────────────────

const EMPTY_FORM = {
  cost_type:    'advertising',
  amount:       '',
  currency:     'USD',
  period_label: '',
  note:         '',
};

function CostDrawer({ product, onClose, onRefresh }) {
  const [costs,   setCosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [form,    setForm]    = useState(EMPTY_FORM);
  const [formErr, setFormErr] = useState('');

  useEffect(() => {
    if (!product) return;
    loadCosts();
  }, [product]); // loadCosts product'a bağımlı, product değişince zaten yeniden çalışır

  async function loadCosts() {
    setLoading(true);
    try {
      const data = await api.getProductCosts(product.offer_id);
      setCosts(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    setFormErr('');
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setFormErr('Geçerli bir tutar giriniz');
      return;
    }
    setSaving(true);
    try {
      await api.addProductCost(product.offer_id, form);
      setForm(EMPTY_FORM);
      await loadCosts();
      onRefresh();
    } catch (err) {
      setFormErr(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Bu maliyet kaydı silinsin mi?')) return;
    setDeleting(id);
    try {
      await api.deleteProductCost(id);
      await loadCosts();
      onRefresh();
    } catch {
      // silent
    } finally {
      setDeleting(null);
    }
  }

  if (!product) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full z-50 w-full max-w-md bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div>
            <div className="text-xs text-gray-400 font-mono mb-0.5">{product.offer_id}</div>
            <div className="text-sm font-semibold text-gray-900 leading-snug">{product.name}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none ml-4">×</button>
        </div>

        {/* Cost list */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mevcut Maliyetler</h3>

          {loading ? (
            <div className="flex justify-center py-6"><Spinner /></div>
          ) : costs.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-6">Henüz maliyet kaydı yok</div>
          ) : (
            <div className="space-y-2">
              {costs.map((c) => {
                const ct = COST_TYPE_MAP[c.cost_type] || COST_TYPE_MAP.other;
                return (
                  <div key={c.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <span className="text-lg shrink-0 mt-0.5">{ct.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-800">{ct.label}</span>
                        {c.period_label && (
                          <span className="text-xs text-gray-400">{c.period_label}</span>
                        )}
                      </div>
                      <div className="text-sm font-bold text-gray-900 mt-0.5">
                        {parseFloat(c.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {c.currency}
                      </div>
                      {c.note && (
                        <div className="text-xs text-gray-500 mt-0.5 truncate">{c.note}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {c.created_by} · {new Date(c.created_at).toLocaleDateString('tr-TR')}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(c.id)}
                      disabled={deleting === c.id}
                      className="shrink-0 text-gray-300 hover:text-red-500 transition-colors text-lg leading-none disabled:opacity-40"
                      title="Sil"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Add cost form */}
        <div className="shrink-0 border-t border-gray-100 px-5 py-4 bg-gray-50">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Yeni Maliyet Ekle</h3>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Maliyet Türü</label>
                <select
                  value={form.cost_type}
                  onChange={(e) => setForm({ ...form, cost_type: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                >
                  {COST_TYPES.map((ct) => (
                    <option key={ct.value} value={ct.value}>{ct.icon} {ct.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Döviz</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                >
                  {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Tutar</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Dönem (opsiyonel)</label>
                <input
                  type="text"
                  value={form.period_label}
                  onChange={(e) => setForm({ ...form, period_label: e.target.value })}
                  placeholder="Mayıs 2026"
                  className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Not (opsiyonel)</label>
              <input
                type="text"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Açıklama..."
                className="w-full text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>

            {formErr && (
              <div className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{formErr}</div>
            )}

            <button
              type="submit"
              disabled={saving}
              className="w-full py-2 rounded-xl text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 transition-colors"
            >
              {saving ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Profitability() {
  const [data,          setData]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [search,        setSearch]        = useState('');
  const [sortKey,       setSortKey]       = useState('name');
  const [sortDir,       setSortDir]       = useState('asc');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCsvModal,  setShowCsvModal]  = useState(false);

  async function loadData() {
    try {
      const rows = await api.getProfitability();
      setData(rows);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadData(); }, []);

  // ── Sort & filter ──────────────────────────────────────────────────────────
  function toggleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return data.filter(
      (r) =>
        r.offer_id.toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let va, vb;
      if (sortKey === 'name')             { va = (a.name || '').toLowerCase(); vb = (b.name || '').toLowerCase(); }
      else if (sortKey === 'avg_sale_price') { va = a.avg_sale_price; vb = b.avg_sale_price; }
      else if (sortKey === 'gross_margin_pct') { va = a.gross_margin_pct ?? -Infinity; vb = b.gross_margin_pct ?? -Infinity; }
      else if (sortKey === 'units_sold_90d')   { va = a.units_sold_90d; vb = b.units_sold_90d; }
      else { va = a[sortKey]; vb = b[sortKey]; }

      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  // ── KPI summary ────────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const tracked = data.length;
    const withMargin = data.filter((r) => r.gross_margin_pct !== null);
    const avgGross = withMargin.length
      ? withMargin.reduce((s, r) => s + r.gross_margin_pct, 0) / withMargin.length
      : null;
    const negCount = data.filter((r) => r.net_margin_pct !== null && r.net_margin_pct < 0).length;
    return { tracked, avgGross, negCount };
  }, [data]);

  // ── Sort button helper ─────────────────────────────────────────────────────
  function SortBtn({ label, sk }) {
    const active = sortKey === sk;
    return (
      <button
        onClick={() => toggleSort(sk)}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
          active
            ? 'bg-indigo-600 text-white border-indigo-600'
            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
        }`}
      >
        {label} {active ? (sortDir === 'asc' ? '↑' : '↓') : ''}
      </button>
    );
  }

  // ── Column header with sort ────────────────────────────────────────────────
  function Th({ label, sk, className = '' }) {
    const active = sortKey === sk;
    return (
      <th
        className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap cursor-pointer select-none hover:text-gray-900 ${className}`}
        onClick={() => sk && toggleSort(sk)}
      >
        {label}{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
      </th>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      {/* ── Page header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Karlılık Analizi</h1>
          <p className="text-sm text-gray-500 mt-0.5">Ürün bazlı maliyet ve kar marjı</p>
        </div>
        <button
          onClick={() => setShowCsvModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
        >
          <span>📤</span> CSV İçe Aktar
        </button>
      </div>

      {/* ── KPI bar ── */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4">
          <div className="text-xs font-medium text-gray-500 mb-1">Takip Edilen Ürün</div>
          <div className="text-2xl font-bold text-gray-900">{kpi.tracked}</div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4">
          <div className="text-xs font-medium text-gray-500 mb-1">Ort. Brüt Kar Marjı</div>
          <div className={`text-2xl font-bold ${
            kpi.avgGross === null ? 'text-gray-400' :
            kpi.avgGross < 0 ? 'text-red-600' :
            kpi.avgGross < 20 ? 'text-amber-600' : 'text-green-600'
          }`}>
            {kpi.avgGross === null ? '—' : fmtPct(kpi.avgGross)}
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4">
          <div className="text-xs font-medium text-gray-500 mb-1">Negatif Net Marjlı Ürün</div>
          <div className={`text-2xl font-bold ${kpi.negCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {kpi.negCount}
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Ürün ara (isim veya offer_id)…"
          className="border border-gray-200 rounded-xl px-3.5 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
        />
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 font-medium">Sırala:</span>
          <SortBtn label="İsim" sk="name" />
          <SortBtn label="Maliyet" sk="total_extra_costs" />
          <SortBtn label="Kar Marjı" sk="gross_margin_pct" />
          <SortBtn label="Satış" sk="units_sold_90d" />
        </div>
        <div className="ml-auto text-xs text-gray-400">
          {sorted.length} ürün
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <Th label="Ürün"           sk="name"            />
                <Th label="Ort. Satış"     sk="avg_sale_price"  />
                <Th label="Ort. Alış"                           />
                <Th label="Ek Maliyet/Birim" sk="total_extra_costs" />
                <Th label="Brüt Kar %"     sk="gross_margin_pct" />
                <Th label="Net Kar %"      sk="net_margin_pct"  />
                <Th label="Satılan (90g)"  sk="units_sold_90d"  />
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">
                    {search ? 'Arama sonucu bulunamadı' : 'Ürün yok'}
                  </td>
                </tr>
              )}
              {sorted.map((row) => (
                <ProductRow
                  key={row.offer_id}
                  row={row}
                  onDetail={() => setSelectedProduct(row)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Cost Drawer ── */}
      {selectedProduct && (
        <CostDrawer
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onRefresh={loadData}
        />
      )}

      {/* ── CSV Import Modal ── */}
      {showCsvModal && (
        <CsvImportModal
          onClose={() => setShowCsvModal(false)}
          onDone={() => { loadData(); }}
        />
      )}
    </div>
  );
}

// ─── Product Row ──────────────────────────────────────────────────────────────

function ProductRow({ row, onDetail }) {
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      {/* Ürün */}
      <td className="px-4 py-3 max-w-[200px]">
        <div className="font-semibold text-gray-900 text-xs font-mono truncate">{row.offer_id}</div>
        <div className="text-xs text-gray-500 mt-0.5 truncate">{row.name}</div>
      </td>

      {/* Ort. Satış */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`text-sm font-medium ${row.avg_sale_price > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
          {row.avg_sale_price > 0 ? fmtPrice(row.avg_sale_price) : '—'}
        </span>
      </td>

      {/* Ort. Alış — read-only, info tooltip */}
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <span className={`text-sm font-medium ${row.avg_purchase_price > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
            {row.avg_purchase_price > 0 ? fmtPrice(row.avg_purchase_price) : '—'}
          </span>
          <div className="relative group">
            <span className="text-gray-300 cursor-help text-xs select-none">ⓘ</span>
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 z-10 hidden group-hover:block bg-gray-900 text-white text-xs rounded-lg px-2.5 py-1.5 whitespace-nowrap shadow-lg pointer-events-none">
              Stok girişlerinden hesaplanır
            </div>
          </div>
        </div>
      </td>

      {/* Ek Maliyet/Birim */}
      <td className="px-4 py-3 whitespace-nowrap">
        <button
          onClick={onDetail}
          className="group flex items-center gap-1.5"
          title="Maliyet detayına git"
        >
          <span className={`text-sm font-medium ${row.extra_cost_per_unit > 0 ? 'text-indigo-700 group-hover:text-indigo-900' : 'text-gray-400 group-hover:text-gray-600'}`}>
            {row.extra_cost_per_unit > 0
              ? '$' + parseFloat(row.extra_cost_per_unit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : '—'
            }
          </span>
          {row.cost_entry_count > 0 && (
            <span className="text-xs bg-indigo-100 text-indigo-600 rounded-full px-1.5 py-0.5 font-medium">
              {row.cost_entry_count}
            </span>
          )}
        </button>
      </td>

      {/* Brüt Kar % */}
      <td className="px-4 py-3 whitespace-nowrap">
        {marginBadge(row.gross_margin_pct)}
      </td>

      {/* Net Kar % */}
      <td className="px-4 py-3 whitespace-nowrap">
        {marginBadge(row.net_margin_pct)}
      </td>

      {/* Satılan 90g */}
      <td className="px-4 py-3 whitespace-nowrap">
        <span className={`text-sm font-medium ${row.units_sold_90d > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
          {row.units_sold_90d > 0 ? row.units_sold_90d.toLocaleString('tr-TR') : '—'}
        </span>
      </td>

      {/* İşlem */}
      <td className="px-4 py-3 whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onDetail}
            className="px-2.5 py-1 rounded-lg text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
          >
            Maliyet Ekle
          </button>
          <button
            onClick={onDetail}
            className="px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Detay
          </button>
        </div>
      </td>
    </tr>
  );
}
